/* eslint-disable no-console */
import {Flex, Form, Select} from 'antd';
import React, {useState, isValidElement} from 'react';

import {ReactComponent as PeerAveragePlus} from '../../../../assets/icons/PeerAveragePlus.svg';
import PilComp from '../../../../components/DisplayComponent/PilComp';
import {
  ESGFrameworkDetail,
  PeerAveragePopup
} from '../../../../components/DisplayModal/ESGFramework/ESGFrameworkDetail';
import useCompanyStore from '../../../../store/companyStore/companyDetailsStore';
import {toIdArray} from '../../../../utils/companyCategorySubCategory';
import {OPKey} from '../../../configurator/orgFacility/orgInfo/constants/orgProfileKeyLabel';

import {
  getPeerBenchmarkDataArray,
  resolvePeerBenchmarkMetricView
} from './constants/peerBenchmarkingDataResolver';

interface PeerBenchMarkingContentProps {
  selectedYear: string;
}

function formatEnvironmentalCompanyValue(value: number | string): string {
  const text = String(value).trim();
  if (/^-?\d+(\.\d+)?$/.test(text)) {
    return `${text} tCO2e`;
  }

  return text;
}

const PeerBenchMarkingContent: React.FC<PeerBenchMarkingContentProps> = ({
  selectedYear
}) => {
  const [showEsgDetailModal, setShowEsgDetailModal] = useState(false);
  const [showPeerAveragePopup, setShowPeerAveragePopup] = useState(false);
  const [peerAverageDetail, setPeerAverageDetail] = useState('');
  const [selectedMericsIndex, setSelectedMericsIndex] = useState(0);
  const [selectedCompanyName, setSelectedCompanyName] = useState('');
  const [selectedCompanyDetail, setSelectedCompanyDetail] = useState('');
  const [selectedMetricLabel, setSelectedMetricLabel] = useState<
    Record<number, string>
  >({});
  const [selectedMetricValue, setSelectedMetricValue] = useState<
    Record<string, string>
  >({});

  const companyDetails = useCompanyStore.getState().companyDetails;
  const subCategory = companyDetails[OPKey.SUB_CATEGORY];
  const subCategoryKey =
    toIdArray(subCategory).join(',') || '0';
  const dataArray = getPeerBenchmarkDataArray(subCategory, selectedYear);

  const metricSelectionKey = (index: number) =>
    `${subCategoryKey}-${selectedYear}-${index}`;

  return dataArray?.map((item, index) => {
    const selectionKey = metricSelectionKey(index);
    const defaultMetricValue = item.metrics?.[0]?.value ?? '';
    const metricValue =
      selectedMetricValue[selectionKey] ?? defaultMetricValue;
    const view = resolvePeerBenchmarkMetricView(item, metricValue);

    return (
      <Flex
        style={{
          width: '100%',
          backgroundColor: '#ffffff',
          border: '1px solid #F3F4F8',
          borderRadius: '12px',
          padding: '20px'
        }}
        gap={45}
        key={`${item.pillar}_${index}`}
      >
        <Flex vertical gap={25} style={{width: '40%'}}>
          <PilComp
            value={item?.pillar}
            style={{
              height: '35px',
              alignContent: 'center',
              color: '#000000',
              font: 'normal normal 700 16px Montserrat',
              padding: '8px 16px',
              backgroundColor: item?.color
            }}
          />
          <Form.Item name={''} label="ESG Metric" labelCol={{span: 24}}>
            <Select
              key={selectionKey}
              style={{
                height: '48px',
                font: 'normal normal 700 16px Montserrat'
              }}
              defaultValue={defaultMetricValue}
              options={item?.metrics}
              placeholder="Choose ESG Metric"
              onChange={(
                value: string,
                option:
                  | {label?: string; children?: string}
                  | Array<{label?: string; children?: string}>
              ) => {
                const selectedOption = Array.isArray(option) ? option[0] : option;
                const label =
                  selectedOption?.label ?? selectedOption?.children ?? '';
                setSelectedMetricValue((prev) => ({
                  ...prev,
                  [selectionKey]: value
                }));
                setSelectedMetricLabel((prev) => ({...prev, [index]: label}));
              }}
            />
          </Form.Item>
        </Flex>
        <Flex vertical style={{width: '60%'}} gap={6}>
          <Flex
            style={{
              border: '1px solid #B3B5BD',
              borderRadius: '4px',
              height: '55px',
              backgroundColor: item?.color,
              padding: '12px'
            }}
            align="center"
            justify="space-between"
          >
            <div
              style={{
                color: '#000000',
                font: 'normal normal 700 16px Montserrat',
                alignContent: 'center'
              }}
            >
              Peer Average
            </div>
            <div
              style={{
                borderRadius: '100px',
                backgroundColor: item?.pillColor,
                width: isValidElement(view?.average) ? '32px' : 'fit-content',
                height: isValidElement(view?.average) ? '32px' : 'fit-content',
                padding: isValidElement(view?.average) ? '8px' : '8px 16px',
                font: 'normal normal 700 16px Montserrat'
              }}
              onClick={() => {
                setPeerAverageDetail(view?.averageDetail);
                setShowPeerAveragePopup(true);
              }}
            >
              {view?.average}
            </div>
          </Flex>
          <Flex
            style={{
              border: '1px solid #B3B5BD',
              borderRadius: '4px',
              height: 'auto',
              maxHeight: '138px',
              padding: '12px 10px',
              overflowY: 'scroll'
            }}
            className="light-scroll"
            gap={8}
            vertical
          >
            <div
              style={{
                color: '#000000',
                font: 'normal normal 700 15px Montserrat'
              }}
            >
              Peer Companies
            </div>
            {view?.companyData?.map((innerItem, innerIndex) => {
              return (
                <Flex
                  vertical={false}
                  align="center"
                  justify="space-between"
                  key={`${innerItem.label}_${innerIndex}`}
                >
                  <div
                    style={{
                      color: '#404252',
                      font: 'normal normal 700 14px Montserrat'
                    }}
                  >
                    {innerItem?.label}
                  </div>
                  <div>
                    {item?.isEnvironmental ? (
                      <PilComp
                        value={formatEnvironmentalCompanyValue(
                          innerItem?.value
                        )}
                        style={{
                          color: '#000000',
                          font: 'normal normal 700 12px Montserrat',
                          padding: '4px 8px',
                          backgroundColor: item?.color
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          height: '24px',
                          aspectRatio: '1',
                          borderRadius: '100px',
                          border: '1px solid #719A5D',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}
                        onClick={() => {
                          setSelectedMericsIndex(index);
                          setSelectedCompanyName(innerItem?.label);
                          setSelectedCompanyDetail(innerItem?.detail);
                          setShowEsgDetailModal(true);
                        }}
                      >
                        <PeerAveragePlus />
                      </div>
                    )}
                  </div>
                </Flex>
              );
            })}
          </Flex>
        </Flex>
        <>
          <ESGFrameworkDetail
            companyName={selectedCompanyName}
            headerTitle={selectedMetricLabel[selectedMericsIndex]}
            contentDetail={selectedCompanyDetail}
            open={showEsgDetailModal}
            onClose={() => {
              setShowEsgDetailModal(false);
            }}
          />
          <PeerAveragePopup
            contentDetail={peerAverageDetail}
            open={showPeerAveragePopup}
            onClose={() => {
              setShowPeerAveragePopup(false);
            }}
          />
        </>
      </Flex>
    );
  });
};

export default PeerBenchMarkingContent;
